import { convertToRaw, EditorState } from 'draft-js';

import type { MentionData } from '../consts';

// What are we doing here???
// The structure of rawData is as follows:
// {
//   entityMap: {
//     0: {
//       data: { mention: ... }
//     },
//     1: {
//       data: { mention: ... }
//     }
//   },
//   blocks: [{
//     text: '...',
//     entityRanges: [{
//       key: 0,
//       offset: ...,
//       length: ...
//     }]
//   }, {
//     text: '...',
//     entityRanges: [{
//       key: 1,
//       offset: ...,
//       length: ...
//     }]
//   }]
// }
// Where each "key" in an "entityRange" of each block corresponds to a unique key in the "entityMap"!
// If you'll look closely, you'll see that each "entityRange" contains "offset" and "length" that corresponds to that block text.
// Meaning that entity begins at offset X and it's Y long in the block text.
// The problem start when we want to map each "entity" in the text to a different value as a change,
// we want to save mention "@ori" as "@("type":"user","id":"123","name":"ori"}" where 123 is the key of that mention.
// Why is that a problem?
// If we'll immediately replace the contents of the block text with this value we'll be out of sync with the "offset" and "length" in the original text.
//
// So what do we actually do?
// We go over each block on all of its entity ranges and we aggregate the block text like so:
// for each entity range we add the text between the previous entity range and the current one and append the formatted mention.
// Then we append the text between the last entity range and the end of the block text (remaining text that doesn't contain mentions)
//
// For example, given the string "this is just some mention @Ori Riner has done, FYI @Daniel Gefen please note the change."
// This is how the iteration of the text will be:
// "this is just some mention @{"type":"user", "id":"123", "name":"Ori Riner" }" ->
// "this is just some mention @{"type":"user", "id":"123", "name":"Ori Riner" } has done, FYI @{"type":"user", "id":"456", "name":"Daniel Gefen" }" ->
// "this is just some mention @{"type":"user", "id":"123", "name":"Ori Riner" } has done, FYI @{"type":"user", "id":"456", "name":"Daniel Gefen" } please note the change."
export function convertToRawWithMentions(
  nextEditorState: EditorState,
  mentionFormatter: (mention: MentionData) => string,
) {
  const raw = convertToRaw(nextEditorState.getCurrentContent());
  return raw.blocks
    .map((block) => {
      let lastIndexOfEntity = 0;
      const mentionBlockText = block.entityRanges.reduce(
        (blockText, entityRange) => {
          const entity = raw.entityMap[entityRange.key];
          if (entity.type !== 'mention') {
            return blockText;
          }
          const formattedMention = mentionFormatter(entity.data.mention);
          const textBetweenPreviousAndCurrentMention = block.text.slice(
            lastIndexOfEntity,
            entityRange.offset,
          );

          blockText += textBetweenPreviousAndCurrentMention + formattedMention;
          lastIndexOfEntity = entityRange.offset + entityRange.length;

          return blockText;
        },
        '',
      );

      return mentionBlockText + block.text.slice(lastIndexOfEntity);
    })
    .join('\n');
}
